import React from 'react'
import styled from 'styled-components'
import { Modal } from '../../../../../molecules/Modal'

const StyledModal = styled(Modal)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`

interface ImagePickerModalProps {
  isOpen: boolean
  onClose: () => void
  images: string[]
  onSelectImage: (imageUrl: string) => void
  onUnselectImage?: () => void
  /** The image currently assigned to the clicked slot */
  currentImage?: string
  selectedImages?: string[]
}

const ModalContent = styled.div`
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
`

const ModalTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
`

const CurrentImageSection = styled.div`
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
`

const CurrentImageLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
`

const CurrentImageWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const CurrentImagePreview = styled.img`
  width: 120px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  border: 2px solid #2563eb;
`

const RemoveButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #ef4444;
  border-radius: 6px;
  background: white;
  color: #ef4444;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #fef2f2;
  }
`

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  max-height: 320px;
  overflow-y: auto;
  min-width: 0;
`

const ImageItem = styled.div<{ $isSelected?: boolean; $isCurrent?: boolean }>`
  position: relative;
  padding-bottom: 75%; /* 4:3 aspect ratio */
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  min-width: 0;
  border: 3px solid
    ${({ $isCurrent, $isSelected }) =>
      $isCurrent ? '#2563eb' : $isSelected ? '#94a3b8' : 'transparent'};
  opacity: ${({ $isSelected, $isCurrent }) =>
    $isSelected && !$isCurrent ? 0.5 : 1};
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`

const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const SelectedBadge = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  background: #2563eb;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`

const UsedBadge = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  background: #94a3b8;
  color: white;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 9px;
  font-weight: 500;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: #64748b;
`

const AvailableLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
`

export const ImagePickerModal: React.FC<ImagePickerModalProps> = ({
  isOpen,
  onClose,
  images,
  onSelectImage,
  onUnselectImage,
  currentImage,
  selectedImages = [],
}) => {
  const handleSelect = (imageUrl: string) => {
    onSelectImage(imageUrl)
  }

  const handleUnselect = () => {
    if (onUnselectImage) {
      onUnselectImage()
    }
  }

  // Images used in other slots (not the current one)
  const otherSelectedImages = selectedImages.filter(
    (img) => img && img !== currentImage
  )

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onClose}
      width="600px"
      maxWidth="600px"
    >
      <ModalContent>
        <ModalTitle>Sélectionner une image</ModalTitle>

        {/* Show current image with remove option */}
        {currentImage && (
          <CurrentImageSection>
            <CurrentImageLabel>Image actuelle</CurrentImageLabel>
            <CurrentImageWrapper>
              <CurrentImagePreview src={currentImage} alt="Image actuelle" />
              <RemoveButton onClick={handleUnselect}>
                Retirer l&apos;image
              </RemoveButton>
            </CurrentImageWrapper>
          </CurrentImageSection>
        )}

        {/* Available images grid */}
        {images.length > 0 ? (
          <>
            <AvailableLabel>
              {currentImage ? 'Choisir une autre image' : 'Choisir une image'}
            </AvailableLabel>
            <ImageGrid>
              {images.map((imageUrl, index) => {
                const isCurrent = imageUrl === currentImage
                const isUsedElsewhere = otherSelectedImages.includes(imageUrl)
                return (
                  <ImageItem
                    key={index}
                    $isCurrent={isCurrent}
                    $isSelected={isCurrent || isUsedElsewhere}
                    onClick={() => !isUsedElsewhere && handleSelect(imageUrl)}
                    style={{
                      cursor: isUsedElsewhere ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <Image src={imageUrl} alt={`Option ${index + 1}`} />
                    {isCurrent && <SelectedBadge>&#10003;</SelectedBadge>}
                    {isUsedElsewhere && <UsedBadge>utilisée</UsedBadge>}
                  </ImageItem>
                )
              })}
            </ImageGrid>
          </>
        ) : (
          <EmptyState>Aucune image disponible</EmptyState>
        )}
      </ModalContent>
    </StyledModal>
  )
}
